import React, { useContext, useEffect, useState, useRef } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  BannerBackgroundImage,
  DateButton,
  Dialog,
  GroupWatchingDialog,
  Interaction,
  LivestreamOverlays,
  SummariseVideoViaAI
} from 'components';
import Vimeo from '@vimeo/player';
import { useIntersection } from 'hooks';
import { hexToRGB } from 'utils';
import { LocalContext, FirebaseContext } from 'context';
import { useLocation } from '@reach/router';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import throttle from 'just-throttle';
import { Markup } from 'interweave';

const detailsSlideVariants = {
  enter: {
    x: 1000,
    opacity: 0
  },
  center: {
    x: 0,
    opacity: 1
  },
  exit: {
    x: -1000,
    opacity: 0,
    transition: {
      x: { duration: 0.75 }
    }
  }
};

const detailsFadeVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 }
};

function Livestream({
  colors,
  tabs,
  eventId,
  eventName,
  eventStartTime,
  eventEndTime,
  eventLocation,
  eventDescription,
  eventDates,
  eventStreamPlaceholderImg,
  eventBannerVideo,
  isChatEnabled,
  isQAndAEnabled,
  isPollsEnabled,
  isParticipantsEnabled,
  selectedEventTab,
  setSelectedEventTab,
  setIsGroupWatchingEnabled,
  eventStatus,
  summariseVideoViaAI
}) {
  const {
    streamRef,
    showGroupWatchingDialog,
    setShowGroupWatchingDialog,
    dialogAnimationTime,
    browserTabIsActive
  } = useContext(LocalContext);
  const { firebase, user } = useContext(FirebaseContext);
  const [vimeoPlayer, setVimeoPlayer] = useState(null);
  const [isStreamMuted, setIsStreamMuted] = useState(null);
  const [livestreamIsPlaying, setLivestreamIsPlaying] = useState(false);
  const [pipRef, vimeoPlayerObserver] = useIntersection({
    initialInView: true,
    threshold: 0.75
  });

  const [isPipModeEnabled, setIsPipModeEnabled] = useState(false);
  const [questionCurrentlyBeingAnsweredLive, setQuestionCurrentlyBeingAnsweredLive] =
    useState(null);
  const [showOnlinePresenceToUsers, setShowOnlinePresenceToUsers] = useState(false);
  const [activeTab, setActiveTab] = useState('Chat');
  const [forcedActiveTab, setForcedActiveTab] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const [hoveredEventTab, setHoveredEventTab] = useState(null);
  const [currentlyOpenWordCloudPollId, setCurrentlyOpenWordCloudPollId] = useState(null);

  const livestreamRef = useRef(null);
  const groupWatchingDialogRef = useRef(null);

  const { state: locationState } = useLocation();
  const referrer = locationState?.referrer;

  const queryString = `dnt=1&pip=1&autoplay=1&fullscreen=1&controls=1&volume=1&muted=${
    referrer ? 0 : 1
  }`;

  const hasTabs = tabs && tabs.length > 1;

  // WAI-ARIA tabs pattern: ArrowLeft/ArrowRight (and Home/End) move between stages, skipping
  // disabled ones, and selection follows focus. Roving tabindex keeps only the active tab in
  // the page's Tab sequence.
  const handleTablistKeyDown = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();

    const enabledTabIndexes = tabs.flatMap((tab, i) => (tab.disabled ? [] : [i]));
    if (!enabledTabIndexes.length) return;

    const currentPosition = enabledTabIndexes.indexOf(selectedEventTab);
    let nextTabIndex;
    if (e.key === 'Home') {
      [nextTabIndex] = enabledTabIndexes;
    } else if (e.key === 'End') {
      nextTabIndex = enabledTabIndexes[enabledTabIndexes.length - 1];
    } else if (e.key === 'ArrowRight') {
      nextTabIndex = enabledTabIndexes[(currentPosition + 1) % enabledTabIndexes.length];
    } else {
      nextTabIndex =
        enabledTabIndexes[
          (currentPosition - 1 + enabledTabIndexes.length) % enabledTabIndexes.length
        ];
    }

    setSelectedEventTab(nextTabIndex);
    document.getElementById(`stage-tab-${tabs[nextTabIndex].eventId}`)?.focus();
  };

  const shouldReduceMotion = useReducedMotion();
  const variants = shouldReduceMotion ? detailsFadeVariants : detailsSlideVariants;

  /* TODO: Refactor/fix this */
  // useEffect(() => {
  //   const keyDownListener = (e) => {
  //     if (e.code === 'Enter' || e.code === 'NumpadEnter') {
  //       if (activeTab === 'Chat') {
  //         handleUserCommentSubmit(e);
  //       } else if (activeTab === 'Q&A') {
  //         handleSubmitNewQuestion(e);
  //       }
  //     }
  //   };
  //   document.addEventListener('keydown', keyDownListener);
  //   return () => {
  //     document.removeEventListener('keydown', keyDownListener);
  //   };
  // }, [activeTab, userCommentText, textArea]);

  useEffect(() => {
    let unsubscribeFromEventUpdates;

    if (firebase && browserTabIsActive) {
      unsubscribeFromEventUpdates = firebase.subscribeToEventUpdates({
        eventId,
        snapshot: (snapshot) => {
          if (snapshot.exists()) {
            const {
              showOnlinePresenceToUsers: _showOnlinePresenceToUsers,
              forcedActiveTab: _forcedActiveTab,
              questionCurrentlyBeingAnsweredLive: _questionCurrentlyBeingAnsweredLive,
              currentlyOpenWordCloudPollId: _currentlyOpenWordCloudPollId,
              groupWatchingIsEnabled: _groupWatchingIsEnabled,
              streamUrl: _streamUrl
            } = snapshot.data();

            setShowOnlinePresenceToUsers(_showOnlinePresenceToUsers);

            if (_forcedActiveTab || _forcedActiveTab === null) {
              setForcedActiveTab(_forcedActiveTab);
            }

            if (_questionCurrentlyBeingAnsweredLive) {
              setQuestionCurrentlyBeingAnsweredLive(_questionCurrentlyBeingAnsweredLive);
            } else {
              setQuestionCurrentlyBeingAnsweredLive(null);
            }

            if (_currentlyOpenWordCloudPollId) {
              setCurrentlyOpenWordCloudPollId(_currentlyOpenWordCloudPollId);
            } else {
              setCurrentlyOpenWordCloudPollId(null);
            }

            if (_groupWatchingIsEnabled) {
              setIsGroupWatchingEnabled(_groupWatchingIsEnabled);
            } else {
              setIsGroupWatchingEnabled(null);
            }

            if (_streamUrl) {
              setStreamUrl(_streamUrl);
            } else {
              setStreamUrl(null);
            }
          } else if (!snapshot.exists()) {
            /* CONOR TODO: Is there a better way of doing this? Can we automatically add the event doc somehow? Hmmmm */
            console.error(
              "You need to add a doc for this event in the 'Events' collection in Firebase."
            );
          }
        }
      });
    }

    return () => {
      if (unsubscribeFromEventUpdates) {
        unsubscribeFromEventUpdates();
      }
    };
  }, [firebase, browserTabIsActive, eventId]);

  // Without these resets the previous stage's stream and moderation state would keep rendering
  // under the new tab until (or unless) the new event doc's snapshot arrives. They're scoped to
  // eventId changes only - the subscription effect above also re-runs on browserTabIsActive
  // toggles (any alt-tab), and resetting streamUrl there would unmount and autoplay-restart the
  // stream every time the user briefly leaves the tab.
  useEffect(
    () => () => {
      setStreamUrl(null);
      setShowOnlinePresenceToUsers(false);
      setForcedActiveTab(null);
      setQuestionCurrentlyBeingAnsweredLive(null);
      setCurrentlyOpenWordCloudPollId(null);
      setIsGroupWatchingEnabled(null);
    },
    [eventId]
  );

  useEffect(() => {
    const livestreamIframe = document.getElementById('livestream-iframe');
    let _vimeoPlayer;

    if (livestreamIframe && user && eventId && streamUrl) {
      _vimeoPlayer = new Vimeo(livestreamIframe);

      setVimeoPlayer(_vimeoPlayer);

      // I'm defining a video "session" as a discrete period of time someone is on the page watching
      // a video. If someone watches an hour one day, then navigates away and comes back to the same
      // page the next day, then those are two seperate sessions. Likewise, if they watch 20 mins
      // then navigate to another part of the site and then come back to the same page again and
      // watches another 20 mins, then those are two seperate sessions. But if someone watches 20
      // mins and pauses the video, then unpauses and continues watching, then that's a single
      // session. Likewise, if someone watches 20 mins and then fast-forwards an hour and continues
      // watching, then that's also a single session.
      const updateVideoSessionData = async () => {
        const data = await _vimeoPlayer.getPlayed();

        const timeRange = data.map((arr) => ({
          start: parseFloat(arr[0].toFixed(2)),
          end: parseFloat(arr[1].toFixed(2))
        }));

        return firebase.livestream.updateVideoSessionData({
          uid: user.uid,
          eventId,
          timeRange
        });
      };

      _vimeoPlayer.on('loaded', () => {
        _vimeoPlayer.on('timeupdate', throttle(updateVideoSessionData, 60000));
        _vimeoPlayer.on('play', (data) => {
          // Using an 'if' statement here because when the 'play' event is fired
          // after any 'seek' event 'data' is undefined.
          if (data) {
            setLivestreamIsPlaying(true);
          }
        });
        _vimeoPlayer.on('pause', (data) => {
          setLivestreamIsPlaying(false);

          // The 'pause' event is also fired when the video ends, along with the 'ended' event.
          // We want to ignore it when the video has ended, so we'll check the percent value.
          if (data.percent !== 1) {
            updateVideoSessionData();
          }
        });
        _vimeoPlayer.on('seeked', updateVideoSessionData);
        _vimeoPlayer.on('ended', updateVideoSessionData);

        _vimeoPlayer.on('enterpictureinpicture', () => {
          setIsPipModeEnabled(true);
        });
        _vimeoPlayer.on('leavepictureinpicture', () => {
          setIsPipModeEnabled(false);
        });
      });
    }

    return () => {
      // Detach from the instance this effect created (the vimeoPlayer state would be one run
      // stale here), otherwise on a stage switch the old 'timeupdate' handler survives and keeps
      // writing video session data against the previous stage's eventId.
      _vimeoPlayer?.off('loaded');
      _vimeoPlayer?.off('timeupdate');
      _vimeoPlayer?.off('play');
      _vimeoPlayer?.off('pause');
      _vimeoPlayer?.off('seeked');
      _vimeoPlayer?.off('ended');
      _vimeoPlayer?.off('enterpictureinpicture');
      _vimeoPlayer?.off('leavepictureinpicture');
      setVimeoPlayer(null);
    };
    // streamUrl is a dependency because the iframe unmounts while it's null and remounts with the
    // new src when the next event doc snapshot arrives - the player must re-attach then.
  }, [user, eventId, streamUrl]);

  useEffect(() => {
    if (
      document.pictureInPictureEnabled &&
      vimeoPlayer &&
      !vimeoPlayer.disablePictureInPicture &&
      livestreamIsPlaying &&
      !vimeoPlayerObserver.isIntersecting
    ) {
      try {
        vimeoPlayer.requestPictureInPicture();
      } catch (err) {
        console.error(err);
      }
    }
  }, [vimeoPlayer, vimeoPlayerObserver]);

  const handlePictureInPictureOverlayClick = () => vimeoPlayer.exitPictureInPicture();

  useEffect(() => {
    if (vimeoPlayer) {
      vimeoPlayer.ready().then(() => {
        vimeoPlayer.getMuted().then((muted) => {
          if (muted) setIsStreamMuted(muted);
        });
      });
    }
  }, [vimeoPlayer]);

  useEffect(() => {
    if (vimeoPlayer) {
      vimeoPlayer.on('volumechange', () => {
        if (isStreamMuted) setIsStreamMuted(false);
      });
    }
  }, [vimeoPlayer, isStreamMuted]);

  const handleUnmuteOverlayClick = () => {
    if (isStreamMuted) {
      vimeoPlayer.setMuted(false).then(() => setIsStreamMuted(false));
    }
  };

  return (
    <>
      <Container colors={colors}>
        <Banner>
          {eventBannerVideo ? (
            <BannerVideo autoPlay muted id="bgvid">
              <source src={eventBannerVideo} type="video/mp4" />
            </BannerVideo>
          ) : (
            <BannerBackgroundImage colors={colors} />
          )}
        </Banner>
        <AnimatePresence initial={false} exitBeforeEnter>
          <Details
            key={hasTabs ? selectedEventTab : 'event-details'}
            {...(hasTabs
              ? {
                  variants,
                  initial: 'enter',
                  animate: 'center',
                  exit: 'exit',
                  transition: shouldReduceMotion
                    ? { opacity: { duration: 0.25 } }
                    : {
                        x: { type: 'spring', stiffness: 300, damping: 30 },
                        opacity: { duration: 0.1 }
                      }
                }
              : {})}>
            <div ref={streamRef} style={{ position: 'absolute', top: '-4.25rem' }} />
            <Dates colors={colors}>
              <Markup content={eventDates} />
            </Dates>
            <Title colors={colors} tabs={hasTabs}>
              <Markup content={hasTabs ? eventName : `Welcome to ${eventName}`} />
            </Title>
          </Details>
        </AnimatePresence>
        <LivestreamAndInteraction>
          <LiveStream ref={livestreamRef} id="livestream">
            {hasTabs && (
              <EventTabs
                $count={tabs.length}
                role="tablist"
                aria-label="Event stages"
                onKeyDown={handleTablistKeyDown}>
                {tabs.map((tab, i) => {
                  const stageTitle = tab.title || tab.name || '';
                  const isTruncated = stageTitle.length > 18;
                  return (
                    <EventTab
                      key={tab.eventId}
                      id={`stage-tab-${tab.eventId}`}
                      type="button"
                      role="tab"
                      aria-selected={selectedEventTab === i}
                      aria-controls="livestream-stage-panel"
                      aria-label={stageTitle}
                      tabIndex={selectedEventTab === i ? 0 : -1}
                      disabled={tab.disabled}
                      onClick={() => setSelectedEventTab(i)}
                      onMouseEnter={() => setHoveredEventTab(i)}
                      onMouseLeave={() => setHoveredEventTab(null)}
                      onFocus={() => setHoveredEventTab(i)}
                      onBlur={() => setHoveredEventTab(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setHoveredEventTab(null);
                        }
                      }}
                      $active={selectedEventTab === i}
                      $colors={tab.colors}>
                      {isTruncated ? `${stageTitle.substring(0, 17)}…` : stageTitle}
                      <AnimatePresence>
                        {isTruncated && hoveredEventTab === i && (
                          <Tooltip
                            aria-hidden="true"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25, type: 'tween', ease: 'easeInOut' }}>
                            <span>{stageTitle}</span>
                          </Tooltip>
                        )}
                      </AnimatePresence>
                    </EventTab>
                  );
                })}
              </EventTabs>
            )}
            <div
              ref={pipRef}
              id="livestream-stage-panel"
              role={hasTabs ? 'tabpanel' : undefined}
              aria-labelledby={hasTabs ? `stage-tab-${tabs[selectedEventTab].eventId}` : undefined}
              style={{
                padding: '56.25% 0 0 0',
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}>
              {eventStreamPlaceholderImg ? (
                <div
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #bdbdbd',
                    height: '100%',
                    left: 0,
                    position: 'absolute',
                    top: 0,
                    width: '100%'
                  }}>
                  <GatsbyImage
                    image={getImage(eventStreamPlaceholderImg)}
                    alt={`${eventName} Stream Placeholder`}
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
              ) : (
                <>
                  {streamUrl && (
                    <iframe
                      title={eventName}
                      id="livestream-iframe"
                      src={
                        streamUrl.includes('?')
                          ? `${streamUrl}&${queryString}`
                          : `${streamUrl}?${queryString}`
                      }
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      referrerPolicy="strict-origin"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                      }}
                    />
                  )}
                  <LivestreamOverlays
                    colors={colors}
                    currentlyOpenWordCloudPollId={currentlyOpenWordCloudPollId}
                    eventId={eventId}
                    handlePictureInPictureOverlayClick={handlePictureInPictureOverlayClick}
                    handleUnmuteOverlayClick={handleUnmuteOverlayClick}
                    isPipModeEnabled={isPipModeEnabled}
                    isStreamMuted={isStreamMuted}
                    livestreamRef={livestreamRef}
                    questionCurrentlyBeingAnsweredLive={questionCurrentlyBeingAnsweredLive}
                  />
                </>
              )}
            </div>
          </LiveStream>
          {isChatEnabled && (
            <Interaction
              colors={colors}
              eventId={eventId}
              showOnlinePresenceToUsers={showOnlinePresenceToUsers}
              forcedActiveTab={forcedActiveTab}
              questionCurrentlyBeingAnsweredLive={questionCurrentlyBeingAnsweredLive}
              isQAndAEnabled={isQAndAEnabled}
              isPollsEnabled={isPollsEnabled}
              isParticipantsEnabled={isParticipantsEnabled}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          )}
        </LivestreamAndInteraction>
        <BelowLivestreamButtons eventStatus={eventStatus}>
          {summariseVideoViaAI && eventStatus === 'previous' && (
            <SummariseVideoViaAI streamUrl={streamUrl} />
          )}
          {eventStatus === 'upcoming' && (
            <DateButton
              eventName={eventName}
              eventDescription={eventDescription}
              eventLocation={eventLocation}
              eventEndTime={eventEndTime}
              eventStartTime={eventStartTime}
              colors={colors}
            />
          )}
        </BelowLivestreamButtons>
      </Container>
      <AnimatePresence initial={false} mode="wait">
        <Dialog ref={groupWatchingDialogRef} isVisible={showGroupWatchingDialog}>
          <GroupWatchingDialog
            colors={colors}
            question={`Are you ${activeTab === 'Participants' ? 'still ' : ''}watching as a group?`}
            setShowGroupWatchingDialog={setShowGroupWatchingDialog}
            groupWatchingDialogRef={groupWatchingDialogRef}
          />
        </Dialog>
      </AnimatePresence>
    </>
  );
}

const BelowLivestreamButtons = styled.div`
  grid-column: 1/7;
  margin-bottom: 1rem;
  margin-right: ${({ eventStatus }) => (eventStatus === 'previous' ? '1.5rem' : 0)};

  @media only screen and (min-width: 48rem) {
    grid-column: 2/6;
  }

  @media only screen and (min-width: 1150px) {
    grid-column: 2/8;
  }
`;

const Dates = styled.h5`
  color: ${({ colors, theme }) =>
    theme.className === 'contrast' ? theme.tertiary : colors.primary};
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.4em;
  margin-bottom: 0;
  margin-top: 0px;

  @media only screen and (min-width: 1150px) {
    font-size: 1.5rem;
  }
`;

const Title = styled.h1`
  color: ${({ colors, theme }) =>
    theme.className === 'contrast' ? theme.primary : colors.secondary};
  font-size: 2rem;
  font-weight: 600;
  line-height: 1.4em;
  margin: ${({ tabs }) => (tabs ? '0 0 2rem' : '0')};

  @media only screen and (min-width: 1150px) {
    font-size: 3rem;
  }
`;

const Details = styled(motion.div)`
  display: flex;
  flex-direction: column;
  grid-column: 1/7;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  @media only screen and (min-width: 48rem) {
    grid-column: 2/5;
    margin-bottom: 1rem;
    margin-top: 3rem;
  }
  @media only screen and (min-width: 1150px) {
    grid-column: 2/8;
    margin-bottom: 3rem;
    margin-top: 0;
  }
`;

const LiveStream = styled.div`
  height: auto;
  margin: 1.25rem -0.75rem;
  position: relative;
  z-index: 2;
  @media only screen and (min-width: 48rem) {
    margin: 1.25rem 0;
  }
  @media only screen and (min-width: 1150px) {
    height: 100%;
    margin: 0px;
  }
`;

const BannerVideo = styled.video`
  height: 100%;
  left: 0;
  object-fit: cover;
  object-position: center;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
`;

const Banner = styled.div`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 0;
`;

const LivestreamAndInteraction = styled.div`
  display: grid;
  grid-column: 1/7;
  grid-gap: 1rem;
  grid-template-columns: 1fr;
  width: 100%;
  @media only screen and (min-width: 48rem) {
    grid-column: 2/6;
  }
  @media only screen and (min-width: 1150px) {
    grid-column: 1/13;
    grid-template-columns: 2fr 1fr;
  }
`;

const Container = styled.div`
  color: #00004e;
  display: grid;
  grid-column: 1/7;
  grid-gap: 1rem;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: auto;
  height: auto;
  margin: 0 auto;
  padding: 1.25rem 0 0;
  width: 100%;

  @media only screen and (min-width: 1150px) {
    grid-column: 1/13;
    grid-template-columns: repeat(12, 1fr);
  }
`;

const EventTabs = styled.div`
  display: grid;
  grid-template-columns: ${({ $count }) => `repeat(${$count}, auto)`};
  left: -1em;
  position: absolute;
  top: -3.25em;
  z-index: 100;
`;

// Tab colours honour the 'contrast' accessibility theme: yellow-on-black replaces the
// event-branded colours, and the inactive grey is opaque so text contrast doesn't depend on
// whatever sits underneath.
const activeTabBackground = ({ theme, $colors }) =>
  theme.className === 'contrast' ? theme.primary : $colors.primary;
const inactiveTabBackground = () => '#dbdbdb';
const hoverTabBackground = ({ theme, $colors }) =>
  theme.className === 'contrast'
    ? theme.primary
    : hexToRGB({ color: $colors.primary, alpha: 0.25 });

const EventTab = styled(motion.button)`
  align-items: center;
  appearance: none;
  background-color: ${(props) =>
    props.$active ? activeTabBackground(props) : inactiveTabBackground(props)};
  border: 0;
  border-top-left-radius: 1.225em;
  border-top-right-radius: 1.225em;
  bottom: ${({ $active }) => ($active ? '0.75rem' : 0)};
  color: ${({ $active, theme }) =>
    $active ? (theme.className === 'contrast' ? theme.contrast : '#fff') : '#3c3c3c'};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  height: ${({ $active }) => ($active ? '4em' : '3.25em')};
  justify-content: center;
  left: 1em;
  padding: 1.125em;
  position: relative;
  text-align: center;
  transition: all 0.5s ease-in-out;
  width: 100%;
  z-index: ${({ $active }) => ($active ? 101 : 100)};
  &:disabled {
    cursor: default;
    opacity: 0.45;
    pointer-events: none;
  }
  &:focus-visible {
    outline: ${(props) =>
      `3px solid ${
        props.theme.className === 'contrast' ? props.theme.primary : props.$colors.primary
      }`};
    outline-offset: 2px;
  }
  &:hover {
    background-color: ${(props) =>
      props.$active ? activeTabBackground(props) : hoverTabBackground(props)};
    bottom: ${({ $active }) => ($active ? '0.75rem' : '0.25rem')};
    color: ${({ $active, theme }) =>
      theme.className === 'contrast' ? theme.contrast : $active ? '#fff' : '#3c3c3c'};
    height: ${({ $active }) => ($active ? '4rem' : '3.5rem')};
    &:before {
      border-bottom: ${(props) =>
        props.$active
          ? `3.2em solid ${activeTabBackground(props)}`
          : `2.6em solid ${hoverTabBackground(props)}`};
    }
  }
  &:before {
    border-bottom: ${(props) =>
      props.$active
        ? `3.2em solid ${activeTabBackground(props)}`
        : `2.4em solid ${inactiveTabBackground(props)}`};
    border-right: 20px solid transparent;
    bottom: 0;
    content: '';
    height: ${({ $active }) => ($active ? '4em' : '3.25em')};
    left: ${({ $active }) => ($active ? 'calc(100% - 1px)' : '100%')};
    line-height: 90px;
    position: absolute;
    transition: all 0.5s ease-in-out;
    width: 0;
    z-index: 101;
  }
`;

const Tooltip = styled(motion.span)`
  background: ${({ theme }) => (theme.className === 'contrast' ? theme.contrast : '#ffffff')};
  border: 1px solid
    ${({ theme }) => (theme.className === 'contrast' ? theme.primary : 'rgba(0, 0, 0, 0.15)')};
  border-radius: 12px;
  box-shadow: 0px 0.25rem 0.625rem rgba(0, 0, 0, 0.14);
  display: block;
  left: 0;
  padding: 0.25rem 0.5rem;
  position: absolute;
  top: -2.25rem;
  width: max-content;
  span {
    color: ${({ theme }) => (theme.className === 'contrast' ? theme.primary : '#3c3c3c')};
    font-size: 0.875rem;
    font-weight: 400;
  }
`;

export default Livestream;
